import { useState, useEffect } from 'react';
import { useAuthStore } from '../store';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { toast } from 'sonner';
import { 
  Plus, RefreshCw, Trash2, Edit, Shield, MessageSquare, 
  Ban, AlertTriangle, Copy, Users, Bot, Image as ImageIcon,
  Hand, ChevronRight, X, Settings, Link2, List, Search, Sparkles
} from 'lucide-react';
import { ImageSelector } from '@/components/ImageSelector';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Componente de botão de melhoria com IA
function AIEnhanceButton({ text, onEnhance, disabled }) {
  const [loading, setLoading] = useState(false);
  
  const handleEnhance = async () => {
    if (!text?.trim()) {
      toast.error('Digite um texto primeiro');
      return;
    }
    
    setLoading(true);
    try {
      const token = localStorage.getItem('nexus-token');
      const res = await fetch(`${API_URL}/api/ai/enhance-text`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ text })
      });
      
      if (res.ok) {
        const data = await res.json();
        onEnhance(data.enhanced);
        toast.success('Texto melhorado com IA! ✨');
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Erro ao melhorar texto');
      }
    } catch (error) {
      toast.error('Erro ao conectar com IA');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      onClick={handleEnhance}
      disabled={loading || disabled || !text?.trim()}
      className="text-purple-500 hover:text-purple-400 border-purple-500/30 h-7 text-xs"
    >
      {loading ? (
        <>
          <RefreshCw className="w-3 h-3 mr-1 animate-spin" />
          Melhorando...
        </>
      ) : (
        <>
          <Sparkles className="w-3 h-3 mr-1" />
          Melhorar com IA
        </>
      )}
    </Button>
  );
}

export default function GroupManagementPage() {
  const { user } = useAuthStore();
  const [flows, setFlows] = useState([]);
  const [connections, setConnections] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Modal states
  const [flowModalOpen, setFlowModalOpen] = useState(false);
  const [editingFlow, setEditingFlow] = useState(null);
  const [warningsModalOpen, setWarningsModalOpen] = useState(false);
  const [selectedFlowWarnings, setSelectedFlowWarnings] = useState(null);
  const [warnings, setWarnings] = useState([]);
  
  // Form state
  const [flowForm, setFlowForm] = useState({
    name: '',
    connection_id: '',
    group_ids: [],
    active: true,
    // Boas-vindas
    welcome_enabled: false,
    welcome_message: '',
    welcome_image_id: '',
    welcome_delay_seconds: 2,
    welcome_mention_user: true,
    // Anti-spam
    antispam_enabled: false,
    forbidden_words: [],
    antispam_action: 'delete',
    antispam_warn_message: '',
    // Avisos/Ban
    warnings_enabled: false,
    max_warnings: 3,
    warning_message: '',
    ban_message: '',
    reset_warnings_days: 0,
    // Chatbot
    chatbot_enabled: false,
    chatbot_triggers: []
  });
  
  // Trigger form para chatbot
  const [triggerForm, setTriggerForm] = useState({
    trigger_type: 'contains',
    trigger_text: '',
    response_message: '',
    response_image_id: '',
    response_type: 'text',
    buttons: [],
    list_title: '',
    list_button_text: 'Ver Opções',
    footer_text: '',
    delay_seconds: 2
  });
  const [editingTriggerIndex, setEditingTriggerIndex] = useState(null);
  
  // Novo botão form
  const [newButton, setNewButton] = useState({
    label: '',
    button_type: 'text',
    url: '',
    response_message: ''
  });

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('nexus-token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [flowsRes, connectionsRes] = await Promise.all([
        fetch(`${API_URL}/api/group-flows`, { headers }),
        fetch(`${API_URL}/api/connections`, { headers })
      ]);
      
      if (flowsRes.ok) {
        const data = await flowsRes.json();
        setFlows(data);
      }
      
      if (connectionsRes.ok) {
        const data = await connectionsRes.json();
        const connected = data.filter(c => c.status === 'connected');
        setConnections(connected);
        
        // Auto-selecionar se tiver apenas 1 conexão
        if (connected.length === 1) {
          setFlowForm(prev => ({ ...prev, connection_id: connected[0].id }));
        }
      }
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Carregar grupos quando conexão muda
  const loadGroupsForConnection = async (connectionId) => {
    if (!connectionId) {
      setGroups([]);
      return;
    }
    
    try {
      const token = localStorage.getItem('nexus-token');
      const res = await fetch(`${API_URL}/api/connections/${connectionId}/groups`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setGroups(data);
      }
    } catch (error) {
      console.error('Erro ao carregar grupos:', error);
    }
  };

  // Sincronizar grupos da conexão com UAZAPI
  const [syncingGroups, setSyncingGroups] = useState(false);
  
  const handleSyncGroups = async () => {
    if (!flowForm.connection_id) {
      toast.error('Selecione uma conexão primeiro');
      return;
    }
    
    setSyncingGroups(true);
    try {
      const token = localStorage.getItem('nexus-token');
      const res = await fetch(`${API_URL}/api/connections/${flowForm.connection_id}/refresh-groups`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(`${data.count || 0} grupos sincronizados!`);
        // Recarregar a lista de grupos
        await loadGroupsForConnection(flowForm.connection_id);
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Erro ao sincronizar grupos');
      }
    } catch (error) {
      console.error('Erro ao sincronizar:', error);
      toast.error('Erro ao sincronizar grupos');
    } finally {
      setSyncingGroups(false);
    }
  };

  const handleOpenFlowModal = (flow = null) => {
    if (flow) {
      setEditingFlow(flow);
      setFlowForm({
        name: flow.name || '',
        connection_id: flow.connection_id || '',
        group_ids: flow.group_ids || [],
        active: flow.active !== false,
        welcome_enabled: flow.welcome_enabled || false,
        welcome_message: flow.welcome_message || '',
        welcome_image_id: flow.welcome_image_id || '',
        welcome_delay_seconds: flow.welcome_delay_seconds || 2,
        welcome_mention_user: flow.welcome_mention_user !== false,
        antispam_enabled: flow.antispam_enabled || false,
        forbidden_words: flow.forbidden_words || [],
        antispam_action: flow.antispam_action || 'delete',
        antispam_warn_message: flow.antispam_warn_message || '',
        warnings_enabled: flow.warnings_enabled || false,
        max_warnings: flow.max_warnings || 3,
        warning_message: flow.warning_message || '',
        ban_message: flow.ban_message || '',
        reset_warnings_days: flow.reset_warnings_days || 0,
        chatbot_enabled: flow.chatbot_enabled || false,
        chatbot_triggers: flow.chatbot_triggers || []
      });
      loadGroupsForConnection(flow.connection_id);
    } else {
      setEditingFlow(null);
      setFlowForm({
        name: '',
        connection_id: '',
        group_ids: [],
        active: true,
        welcome_enabled: false,
        welcome_message: '',
        welcome_image_id: '',
        welcome_delay_seconds: 2,
        welcome_mention_user: true,
        antispam_enabled: false,
        forbidden_words: [],
        antispam_action: 'delete',
        antispam_warn_message: '',
        warnings_enabled: false,
        max_warnings: 3,
        warning_message: '',
        ban_message: '',
        reset_warnings_days: 0,
        chatbot_enabled: false,
        chatbot_triggers: []
      });
      setGroups([]);
    }
    setFlowModalOpen(true);
  };

  const handleSaveFlow = async () => {
    if (!flowForm.name.trim()) {
      toast.error('Nome do fluxo é obrigatório');
      return;
    }
    
    if (!flowForm.connection_id) {
      toast.error('Selecione uma conexão');
      return;
    }
    
    try {
      const token = localStorage.getItem('nexus-token');
      const url = editingFlow 
        ? `${API_URL}/api/group-flows/${editingFlow.id}`
        : `${API_URL}/api/group-flows`;
      
      // Filtrar palavras proibidas vazias antes de enviar
      const dataToSave = {
        ...flowForm,
        forbidden_words: flowForm.forbidden_words.filter(w => w.trim())
      };
      
      const res = await fetch(url, {
        method: editingFlow ? 'PUT' : 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(dataToSave)
      });
      
      if (res.ok) {
        toast.success(editingFlow ? 'Fluxo atualizado!' : 'Fluxo criado!');
        setFlowModalOpen(false);
        fetchData();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Erro ao salvar fluxo');
      }
    } catch (error) {
      console.error('Erro ao salvar:', error);
      toast.error('Erro ao salvar fluxo');
    }
  };

  const handleDeleteFlow = async (flowId) => {
    if (!confirm('Tem certeza que deseja deletar este fluxo?')) return;
    
    try {
      const token = localStorage.getItem('nexus-token');
      const res = await fetch(`${API_URL}/api/group-flows/${flowId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success('Fluxo deletado!');
        fetchData();
      }
    } catch (error) {
      toast.error('Erro ao deletar fluxo');
    }
  };

  const handleDuplicateFlow = async (flowId) => {
    try {
      const token = localStorage.getItem('nexus-token');
      const res = await fetch(`${API_URL}/api/group-flows/${flowId}/duplicate`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success('Fluxo duplicado!');
        fetchData();
      }
    } catch (error) {
      toast.error('Erro ao duplicar fluxo');
    }
  };

  const handleViewWarnings = async (flow) => {
    setSelectedFlowWarnings(flow);
    try {
      const token = localStorage.getItem('nexus-token');
      const res = await fetch(`${API_URL}/api/group-flows/${flow.id}/warnings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setWarnings(data);
        setWarningsModalOpen(true);
      }
    } catch (error) {
      toast.error('Erro ao carregar avisos');
    }
  };

  const handleClearWarnings = async (phone) => {
    if (!selectedFlowWarnings) return;
    
    try {
      const token = localStorage.getItem('nexus-token');
      await fetch(`${API_URL}/api/group-flows/${selectedFlowWarnings.id}/warnings/${phone}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Avisos limpos!');
      handleViewWarnings(selectedFlowWarnings);
    } catch (error) {
      toast.error('Erro ao limpar avisos');
    }
  };

  const handleUnbanMember = async (phone) => {
    if (!selectedFlowWarnings) return;
    
    try {
      const token = localStorage.getItem('nexus-token');
      await fetch(`${API_URL}/api/group-flows/${selectedFlowWarnings.id}/unban/${phone}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Membro desbanido!');
      handleViewWarnings(selectedFlowWarnings);
    } catch (error) {
      toast.error('Erro ao desbanir');
    }
  };

  // Adicionar/editar trigger do chatbot
  const handleAddTrigger = () => {
    if (!triggerForm.trigger_text.trim() && triggerForm.trigger_type !== 'any') {
      toast.error('Digite o texto do gatilho');
      return;
    }
    
    // Auto-corrigir response_type baseado nos botões
    let finalResponseType = triggerForm.response_type;
    if (triggerForm.buttons && triggerForm.buttons.length > 0) {
      // Se tem botões mas response_type é 'text', mudar para 'buttons'
      if (finalResponseType === 'text') {
        finalResponseType = 'buttons';
      }
    }
    
    const newTrigger = {
      ...triggerForm,
      response_type: finalResponseType,
      id: Date.now().toString()
    };
    
    if (editingTriggerIndex !== null) {
      const updated = [...flowForm.chatbot_triggers];
      updated[editingTriggerIndex] = newTrigger;
      setFlowForm({ ...flowForm, chatbot_triggers: updated });
      setEditingTriggerIndex(null);
    } else {
      setFlowForm({
        ...flowForm,
        chatbot_triggers: [...flowForm.chatbot_triggers, newTrigger]
      });
    }
    
    // Reset trigger form
    setTriggerForm({
      trigger_type: 'contains',
      trigger_text: '',
      response_message: '',
      response_image_id: '',
      response_type: 'text',
      buttons: [],
      list_title: '',
      list_button_text: 'Ver Opções',
      footer_text: '',
      delay_seconds: 2
    });
  };

  const handleEditTrigger = (index) => {
    const trigger = flowForm.chatbot_triggers[index];
    setTriggerForm({
      trigger_type: trigger.trigger_type || 'contains',
      trigger_text: trigger.trigger_text || '',
      response_message: trigger.response_message || '',
      response_image_id: trigger.response_image_id || '',
      response_type: trigger.response_type || 'text',
      buttons: trigger.buttons || [],
      list_title: trigger.list_title || '',
      list_button_text: trigger.list_button_text || 'Ver Opções',
      footer_text: trigger.footer_text || '',
      delay_seconds: trigger.delay_seconds || 2
    });
    setEditingTriggerIndex(index);
  };

  const handleRemoveTrigger = (index) => {
    const updated = flowForm.chatbot_triggers.filter((_, i) => i !== index);
    setFlowForm({ ...flowForm, chatbot_triggers: updated });
  };

  // Adicionar botão ao trigger
  const handleAddButton = () => {
    if (!newButton.label.trim()) {
      toast.error('Digite o texto do botão');
      return;
    }
    
    const button = {
      id: Date.now().toString(),
      ...newButton
    };
    
    // Auto-mudar response_type para 'buttons' se ainda estiver como 'text'
    const newResponseType = triggerForm.response_type === 'text' ? 'buttons' : triggerForm.response_type;
    
    setTriggerForm({
      ...triggerForm,
      buttons: [...triggerForm.buttons, button],
      response_type: newResponseType
    });
    
    setNewButton({ label: '', button_type: 'text', url: '', response_message: '' });
  };

  const handleRemoveButton = (index) => {
    const updated = triggerForm.buttons.filter((_, i) => i !== index);
    setTriggerForm({ ...triggerForm, buttons: updated });
  };

  // Filtrar fluxos
  const filteredFlows = flows.filter(f => 
    f.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    f.connection_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 md:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <Shield className="w-6 h-6 text-primary" />
            Gestão de Grupos
          </h1>
          <p className="text-muted-foreground text-sm">
            Configure boas-vindas, anti-spam, avisos e chatbot para seus grupos
          </p>
        </div>
        
        <div className="flex gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input 
              placeholder="Buscar fluxos..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9 w-64"
            />
          </div>
          <Button onClick={() => handleOpenFlowModal()}>
            <Plus className="w-4 h-4 mr-2" />
            Novo Fluxo
          </Button>
        </div>
      </div>

      {/* Lista de Fluxos */}
      {filteredFlows.length === 0 ? (
        <Card className="glass">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Shield className="w-12 h-12 text-muted-foreground mb-4" />
            <p className="text-lg font-medium">Nenhum fluxo configurado</p>
            <p className="text-sm text-muted-foreground mb-4">
              Crie um fluxo para gerenciar seus grupos
            </p>
            <Button onClick={() => handleOpenFlowModal()}>
              <Plus className="w-4 h-4 mr-2" />
              Criar Primeiro Fluxo
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {filteredFlows.map((flow) => (
            <Card key={flow.id} className="glass hover:border-primary/50 transition-colors">
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <CardTitle className="text-lg truncate">{flow.name}</CardTitle>
                    <CardDescription className="truncate">
                      {flow.connection_name}
                    </CardDescription>
                  </div>
                  <Badge variant={flow.active ? 'default' : 'secondary'}>
                    {flow.active ? 'Ativo' : 'Inativo'}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {/* Features habilitadas */}
                <div className="flex flex-wrap gap-1.5">
                  {flow.antispam_enabled && (
                    <Badge variant="outline" className="text-xs text-red-500 border-red-500/50">
                      <Ban className="w-3 h-3 mr-1" />
                      Anti-spam
                    </Badge>
                  )}
                  {flow.warnings_enabled && (
                    <Badge variant="outline" className="text-xs text-yellow-500 border-yellow-500/50">
                      <AlertTriangle className="w-3 h-3 mr-1" />
                      Avisos ({flow.max_warnings})
                    </Badge>
                  )}
                  {flow.chatbot_enabled && (
                    <Badge variant="outline" className="text-xs text-blue-500 border-blue-500/50">
                      <Bot className="w-3 h-3 mr-1" />
                      Chatbot
                    </Badge>
                  )}
                </div>
                
                {/* Grupos */}
                <div className="text-sm text-muted-foreground">
                  <Users className="w-4 h-4 inline mr-1" />
                  {flow.group_names?.length || 0} grupo(s)
                  {flow.group_names?.length > 0 && (
                    <span className="ml-1 text-xs">
                      ({flow.group_names.slice(0, 2).join(', ')}{flow.group_names.length > 2 ? '...' : ''})
                    </span>
                  )}
                </div>
                
                {/* Ações */}
                <div className="flex gap-2 pt-2 border-t border-border/50">
                  <Button 
                    size="sm" 
                    variant="outline" 
                    className="flex-1"
                    onClick={() => handleOpenFlowModal(flow)}
                  >
                    <Edit className="w-4 h-4 mr-1" />
                    Editar
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => handleDuplicateFlow(flow.id)}
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                  {flow.warnings_enabled && (
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => handleViewWarnings(flow)}
                    >
                      <AlertTriangle className="w-4 h-4" />
                    </Button>
                  )}
                  <Button 
                    size="sm" 
                    variant="destructive"
                    onClick={() => handleDeleteFlow(flow.id)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Modal Criar/Editar Fluxo */}
      <Dialog open={flowModalOpen} onOpenChange={setFlowModalOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingFlow ? 'Editar Fluxo' : 'Novo Fluxo de Gestão'}
            </DialogTitle>
            <DialogDescription>
              Configure as funcionalidades de gestão para seus grupos
            </DialogDescription>
          </DialogHeader>
          
          <Tabs defaultValue="basic" className="w-full">
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="basic">Básico</TabsTrigger>
              <TabsTrigger value="antispam">Anti-spam</TabsTrigger>
              <TabsTrigger value="warnings">Avisos</TabsTrigger>
              <TabsTrigger value="chatbot">Chatbot</TabsTrigger>
            </TabsList>
            
            {/* Tab Básico */}
            <TabsContent value="basic" className="space-y-4 mt-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label>Nome do Fluxo *</Label>
                  <Input
                    placeholder="Ex: Regras do Grupo VIP"
                    value={flowForm.name}
                    onChange={(e) => setFlowForm({ ...flowForm, name: e.target.value })}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Conexão *</Label>
                  <Select
                    value={flowForm.connection_id}
                    onValueChange={(value) => {
                      setFlowForm({ ...flowForm, connection_id: value, group_ids: [] });
                      loadGroupsForConnection(value);
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione a conexão" />
                    </SelectTrigger>
                    <SelectContent>
                      {connections.map((conn) => (
                        <SelectItem key={conn.id} value={conn.id}>
                          {conn.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              {/* Seleção de Grupos */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Grupos para aplicar o fluxo</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleSyncGroups}
                    disabled={!flowForm.connection_id || syncingGroups}
                    className="h-7 text-xs"
                  >
                    {syncingGroups ? (
                      <>
                        <RefreshCw className="w-3 h-3 mr-1 animate-spin" />
                        Sincronizando...
                      </>
                    ) : (
                      <>
                        <RefreshCw className="w-3 h-3 mr-1" />
                        Atualizar Grupos
                      </>
                    )}
                  </Button>
                </div>
                {groups.length === 0 ? (
                  <div className="text-center py-4 border rounded-md">
                    <p className="text-sm text-muted-foreground">
                      {flowForm.connection_id 
                        ? 'Nenhum grupo encontrado. Clique em "Atualizar Grupos" para sincronizar.'
                        : 'Selecione uma conexão para ver os grupos'}
                    </p>
                    {flowForm.connection_id && (
                      <Button
                        type="button"
                        variant="link"
                        size="sm"
                        onClick={handleSyncGroups}
                        disabled={syncingGroups}
                        className="mt-2"
                      >
                        <RefreshCw className={`w-4 h-4 mr-1 ${syncingGroups ? 'animate-spin' : ''}`} />
                        Sincronizar agora
                      </Button>
                    )}
                  </div>
                ) : (
                  <div className="grid gap-2 max-h-48 overflow-y-auto p-2 border rounded-md">
                    {groups.map((group) => (
                      <label 
                        key={group.id}
                        className="flex items-center gap-2 p-2 rounded hover:bg-secondary/50 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={flowForm.group_ids.includes(group.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setFlowForm({
                                ...flowForm,
                                group_ids: [...flowForm.group_ids, group.id]
                              });
                            } else {
                              setFlowForm({
                                ...flowForm,
                                group_ids: flowForm.group_ids.filter(id => id !== group.id)
                              });
                            }
                          }}
                          className="rounded"
                        />
                        <span className="flex-1 truncate">{group.name}</span>
                        <span className="text-xs text-muted-foreground">
                          {group.participants_count} membros
                        </span>
                      </label>
                    ))}
                  </div>
                )}
                {flowForm.group_ids.length > 0 && (
                  <p className="text-sm text-primary">
                    {flowForm.group_ids.length} grupo(s) selecionado(s)
                  </p>
                )}
              </div>
              
              <div className="flex items-center gap-2">
                <Switch
                  checked={flowForm.active}
                  onCheckedChange={(checked) => setFlowForm({ ...flowForm, active: checked })}
                />
                <Label>Fluxo ativo</Label>
              </div>
            </TabsContent>
            
            {/* Tab Anti-spam */}
            <TabsContent value="antispam" className="space-y-4 mt-4">
              <div className="flex items-center gap-2 p-4 bg-secondary/30 rounded-lg">
                <Switch
                  checked={flowForm.antispam_enabled}
                  onCheckedChange={(checked) => setFlowForm({ ...flowForm, antispam_enabled: checked })}
                />
                <div>
                  <Label className="text-base">Anti-spam</Label>
                  <p className="text-sm text-muted-foreground">
                    Deletar mensagens com palavras proibidas
                  </p>
                </div>
              </div>
              
              {flowForm.antispam_enabled && (
                <div className="space-y-4 pl-4 border-l-2 border-red-500/50">
                  <div className="space-y-2">
                    <Label>Palavras Proibidas</Label>
                    <Textarea
                      placeholder="Digite as palavras proibidas, uma por linha"
                      value={flowForm.forbidden_words.join('\n')}
                      onChange={(e) => setFlowForm({ 
                        ...flowForm, 
                        forbidden_words: e.target.value.split('\n')
                      })}
                      rows={6}
                    />
                    <p className="text-xs text-muted-foreground">
                      Uma palavra por linha. Não diferencia maiúsculas/minúsculas.
                    </p>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Ação ao detectar spam</Label>
                    <Select
                      value={flowForm.antispam_action}
                      onValueChange={(value) => setFlowForm({ ...flowForm, antispam_action: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="delete">Apenas deletar mensagem</SelectItem>
                        <SelectItem value="warn">Deletar e avisar membro</SelectItem>
                        <SelectItem value="ban">Deletar e banir imediatamente</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {flowForm.antispam_action !== 'delete' && (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label>Mensagem de Aviso</Label>
                        <AIEnhanceButton 
                          text={flowForm.antispam_warn_message}
                          onEnhance={(enhanced) => setFlowForm({ ...flowForm, antispam_warn_message: enhanced })}
                        />
                      </div>
                      <Textarea
                        placeholder="Atenção {nome}! Não é permitido enviar esse tipo de conteúdo."
                        value={flowForm.antispam_warn_message}
                        onChange={(e) => setFlowForm({ ...flowForm, antispam_warn_message: e.target.value })}
                        rows={2}
                      />
                      <p className="text-xs text-muted-foreground">
                        Use {'{nome}'} para o telefone e {'{palavra}'} para a palavra detectada
                      </p>
                    </div>
                  )}
                </div>
              )}
            </TabsContent>
            
            {/* Tab Avisos/Ban */}
            <TabsContent value="warnings" className="space-y-4 mt-4">
              <div className="flex items-center gap-2 p-4 bg-secondary/30 rounded-lg">
                <Switch
                  checked={flowForm.warnings_enabled}
                  onCheckedChange={(checked) => setFlowForm({ ...flowForm, warnings_enabled: checked })}
                />
                <div>
                  <Label className="text-base">Sistema de Avisos</Label>
                  <p className="text-sm text-muted-foreground">
                    Avisar membros e banir após atingir o limite
                  </p>
                </div>
              </div>
              
              {flowForm.warnings_enabled && (
                <div className="space-y-4 pl-4 border-l-2 border-yellow-500/50">
                  <div className="space-y-2">
                    <Label>Número de avisos antes do ban</Label>
                    <Input
                      type="number"
                      min={1}
                      max={10}
                      value={flowForm.max_warnings}
                      onChange={(e) => setFlowForm({ ...flowForm, max_warnings: parseInt(e.target.value) || 3 })}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label>Mensagem ao receber aviso</Label>
                      <AIEnhanceButton 
                        text={flowForm.warning_message}
                        onEnhance={(enhanced) => setFlowForm({ ...flowForm, warning_message: enhanced })}
                      />
                    </div>
                    <Textarea
                      placeholder="Atenção {nome}! Você recebeu o aviso {avisos}/{max_avisos}. Restam {restantes} avisos antes do ban."
                      value={flowForm.warning_message}
                      onChange={(e) => setFlowForm({ ...flowForm, warning_message: e.target.value })}
                      rows={3}
                    />
                    <p className="text-xs text-muted-foreground">
                      Variáveis: {'{nome}'}, {'{avisos}'}, {'{max_avisos}'}, {'{restantes}'}
                    </p>
                  </div>
                  
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label>Mensagem ao ser banido</Label>
                      <AIEnhanceButton 
                        text={flowForm.ban_message}
                        onEnhance={(enhanced) => setFlowForm({ ...flowForm, ban_message: enhanced })}
                      />
                    </div>
                    <Textarea
                      placeholder="{nome} foi removido do grupo por atingir {avisos} avisos."
                      value={flowForm.ban_message}
                      onChange={(e) => setFlowForm({ ...flowForm, ban_message: e.target.value })}
                      rows={2}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Resetar avisos após (dias)</Label>
                    <Input
                      type="number"
                      min={0}
                      max={365}
                      value={flowForm.reset_warnings_days}
                      onChange={(e) => setFlowForm({ ...flowForm, reset_warnings_days: parseInt(e.target.value) || 0 })}
                    />
                    <p className="text-xs text-muted-foreground">
                      0 = nunca resetar automaticamente
                    </p>
                  </div>
                </div>
              )}
            </TabsContent>
            
            {/* Tab Chatbot */}
            <TabsContent value="chatbot" className="space-y-4 mt-4">
              <div className="flex items-center gap-2 p-4 bg-secondary/30 rounded-lg">
                <Switch
                  checked={flowForm.chatbot_enabled}
                  onCheckedChange={(checked) => setFlowForm({ ...flowForm, chatbot_enabled: checked })}
                />
                <div>
                  <Label className="text-base">Chatbot do Grupo</Label>
                  <p className="text-sm text-muted-foreground">
                    Responder automaticamente a comandos/palavras-chave
                  </p>
                </div>
              </div>
              
              {flowForm.chatbot_enabled && (
                <div className="space-y-4 pl-4 border-l-2 border-blue-500/50">
                  {/* Lista de triggers existentes */}
                  {flowForm.chatbot_triggers.length > 0 && (
                    <div className="space-y-2">
                      <Label>Gatilhos configurados</Label>
                      <div className="space-y-2">
                        {flowForm.chatbot_triggers.map((trigger, index) => (
                          <div key={index} className="flex items-center gap-2 p-3 bg-secondary/30 rounded-lg">
                            <div className="flex-1">
                              <p className="font-medium text-sm">
                                {trigger.trigger_type === 'any' ? 'Qualquer mensagem' : `"${trigger.trigger_text}"`}
                              </p>
                              <p className="text-xs text-muted-foreground truncate">
                                {trigger.response_message?.substring(0, 50)}...
                              </p>
                            </div>
                            <Badge variant="outline" className="text-xs">
                              {trigger.response_type === 'buttons' ? 'Botões' : 
                               trigger.response_type === 'list' ? 'Lista' : 'Texto'}
                            </Badge>
                            <Button size="sm" variant="ghost" onClick={() => handleEditTrigger(index)}>
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => handleRemoveTrigger(index)}>
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Formulário de novo trigger */}
                  <Accordion type="single" collapsible defaultValue="new-trigger">
                    <AccordionItem value="new-trigger">
                      <AccordionTrigger>
                        <span className="flex items-center gap-2">
                          <Plus className="w-4 h-4" />
                          {editingTriggerIndex !== null ? 'Editar Gatilho' : 'Adicionar Gatilho'}
                        </span>
                      </AccordionTrigger>
                      <AccordionContent className="space-y-4 pt-4">
                        <div className="grid gap-4 md:grid-cols-2">
                          <div className="space-y-2">
                            <Label>Tipo de Gatilho</Label>
                            <Select
                              value={triggerForm.trigger_type}
                              onValueChange={(value) => setTriggerForm({ ...triggerForm, trigger_type: value })}
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="exact">Exato</SelectItem>
                                <SelectItem value="contains">Contém</SelectItem>
                                <SelectItem value="starts_with">Começa com</SelectItem>
                                <SelectItem value="any">Qualquer mensagem</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          
                          {triggerForm.trigger_type !== 'any' && (
                            <div className="space-y-2">
                              <Label>Texto(s) do Gatilho</Label>
                              <Textarea
                                placeholder="Ex: oi, olá, hey, eai&#10;Ou um por linha"
                                value={triggerForm.trigger_text}
                                onChange={(e) => setTriggerForm({ ...triggerForm, trigger_text: e.target.value })}
                                rows={2}
                              />
                              <p className="text-xs text-muted-foreground">
                                Separe múltiplos gatilhos por vírgula ou linha. Todos disparam a mesma resposta.
                              </p>
                            </div>
                          )}
                        </div>
                        
                        <div className="space-y-2">
                          <Label>Tipo de Resposta</Label>
                          <Select
                            value={triggerForm.response_type}
                            onValueChange={(value) => setTriggerForm({ ...triggerForm, response_type: value })}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="text">Texto simples</SelectItem>
                              <SelectItem value="buttons">Botões de resposta rápida</SelectItem>
                              <SelectItem value="list">Lista de opções</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        
                        <div className="space-y-2">
                          <div className="flex items-center justify-between">
                            <Label>Mensagem de Resposta</Label>
                            <AIEnhanceButton 
                              text={triggerForm.response_message}
                              onEnhance={(enhanced) => setTriggerForm({ ...triggerForm, response_message: enhanced })}
                            />
                          </div>
                          <Textarea
                            placeholder="Digite a resposta..."
                            value={triggerForm.response_message}
                            onChange={(e) => setTriggerForm({ ...triggerForm, response_message: e.target.value })}
                            rows={3}
                          />
                        </div>
                        
                        {/* Botões/Lista */}
                        {(triggerForm.response_type === 'buttons' || triggerForm.response_type === 'list') && (
                          <div className="space-y-3 p-3 border rounded-lg">
                            <Label className="flex items-center gap-2">
                              {triggerForm.response_type === 'buttons' ? (
                                <><MessageSquare className="w-4 h-4" /> Botões (máx. 3)</>
                              ) : (
                                <><List className="w-4 h-4" /> Opções da Lista (máx. 10)</>
                              )}
                            </Label>
                            
                            {/* Lista de botões existentes */}
                            {triggerForm.buttons.length > 0 && (
                              <div className="space-y-2">
                                {triggerForm.buttons.map((btn, idx) => (
                                  <div key={idx} className="flex items-center gap-2 p-2 bg-secondary/30 rounded">
                                    <span className="flex-1 text-sm">{btn.label}</span>
                                    {btn.button_type === 'url' && (
                                      <Badge variant="outline" className="text-xs">
                                        <Link2 className="w-3 h-3 mr-1" />
                                        URL
                                      </Badge>
                                    )}
                                    <Button size="sm" variant="ghost" onClick={() => handleRemoveButton(idx)}>
                                      <X className="w-4 h-4 text-red-500" />
                                    </Button>
                                  </div>
                                ))}
                              </div>
                            )}
                            
                            {/* Formulário para novo botão */}
                            <div className="space-y-2 p-2 border-t">
                              <div className="grid gap-2 md:grid-cols-2">
                                <Input
                                  placeholder="Texto do botão"
                                  value={newButton.label}
                                  onChange={(e) => setNewButton({ ...newButton, label: e.target.value })}
                                />
                                <Select
                                  value={newButton.button_type}
                                  onValueChange={(value) => setNewButton({ ...newButton, button_type: value })}
                                >
                                  <SelectTrigger>
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="text">Resposta rápida</SelectItem>
                                    <SelectItem value="url">Link/URL</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                              
                              {newButton.button_type === 'url' && (
                                <Input
                                  placeholder="https://exemplo.com"
                                  value={newButton.url}
                                  onChange={(e) => setNewButton({ ...newButton, url: e.target.value })}
                                />
                              )}
                              
                              <Button size="sm" variant="outline" onClick={handleAddButton}>
                                <Plus className="w-4 h-4 mr-1" />
                                Adicionar Botão
                              </Button>
                            </div>
                          </div>
                        )}
                        
                        {triggerForm.response_type === 'list' && (
                          <div className="space-y-2">
                            <Label>Texto do botão da lista</Label>
                            <Input
                              placeholder="Ver Opções"
                              value={triggerForm.list_button_text}
                              onChange={(e) => setTriggerForm({ ...triggerForm, list_button_text: e.target.value })}
                            />
                          </div>
                        )}
                        
                        <div className="grid gap-4 md:grid-cols-2">
                          <ImageSelector
                            value={triggerForm.response_image_id}
                            onChange={(url, id) => setTriggerForm({ ...triggerForm, response_image_id: id || '' })}
                            label="Imagem da Resposta (opcional)"
                            category=""
                          />
                          
                          <div className="space-y-2">
                            <Label>Delay (segundos)</Label>
                            <Input
                              type="number"
                              min={0}
                              max={30}
                              value={triggerForm.delay_seconds}
                              onChange={(e) => setTriggerForm({ ...triggerForm, delay_seconds: parseInt(e.target.value) || 0 })}
                            />
                          </div>
                        </div>
                        
                        <div className="space-y-2">
                          <Label>Rodapé (opcional)</Label>
                          <Input
                            placeholder="Texto do rodapé"
                            value={triggerForm.footer_text}
                            onChange={(e) => setTriggerForm({ ...triggerForm, footer_text: e.target.value })}
                          />
                        </div>
                        
                        <Button onClick={handleAddTrigger} className="w-full">
                          <Plus className="w-4 h-4 mr-2" />
                          {editingTriggerIndex !== null ? 'Salvar Gatilho' : 'Adicionar Gatilho'}
                        </Button>
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                </div>
              )}
            </TabsContent>
          </Tabs>
          
          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setFlowModalOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSaveFlow}>
              {editingFlow ? 'Salvar Alterações' : 'Criar Fluxo'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de Avisos */}
      <Dialog open={warningsModalOpen} onOpenChange={setWarningsModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-yellow-500" />
              Avisos de Membros
            </DialogTitle>
            <DialogDescription>
              {selectedFlowWarnings?.name}
            </DialogDescription>
          </DialogHeader>
          
          {warnings.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <AlertTriangle className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>Nenhum aviso registrado</p>
            </div>
          ) : (
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {warnings.map((warning) => (
                <div key={warning.id} className={`p-4 rounded-lg border ${warning.banned ? 'bg-red-500/10 border-red-500/50' : 'bg-yellow-500/10 border-yellow-500/50'}`}>
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-medium">{warning.phone}</p>
                      <p className="text-sm text-muted-foreground">
                        {warning.warning_count} aviso(s)
                        {warning.banned && <Badge variant="destructive" className="ml-2">Banido</Badge>}
                      </p>
                      <div className="mt-2 space-y-1">
                        {warning.reasons?.map((reason, i) => (
                          <p key={i} className="text-xs text-muted-foreground">
                            {i + 1}. {reason}
                          </p>
                        ))}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      {warning.banned ? (
                        <Button size="sm" variant="outline" onClick={() => handleUnbanMember(warning.phone)}>
                          Desbanir
                        </Button>
                      ) : (
                        <Button size="sm" variant="outline" onClick={() => handleClearWarnings(warning.phone)}>
                          Limpar Avisos
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
